/**
 * TokenUsagePie - Circular progress indicator for token usage in chat
 */

import React from 'react';
import { CLAUDE_SONNET_4, CLAUDE_OPUS_4, CLAUDE_HAIKU_4_5 } from '@pistachiorama/shared-types';

// Context window sizes for Claude models
const CONTEXT_WINDOWS: Record<string, number> = {
  // Claude 4.x family
  [CLAUDE_SONNET_4]: 200000,
  [CLAUDE_OPUS_4]: 200000,
  [CLAUDE_HAIKU_4_5]: 200000,
  // Legacy Claude 3.x aliases (kept for backward compatibility)
  'claude-3-5-haiku-latest': 200000,
  'claude-3-5-sonnet-latest': 200000,
  'claude-3-opus-latest': 200000,
  'default': 200000,
};

interface TokenUsagePieProps {
  sessionTokens: number;
  model?: string;
  size?: 'sm' | 'md';
}

function formatTokens(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toString();
}

export function TokenUsagePie({ sessionTokens, model = 'claude-3-5-haiku-latest', size = 'sm' }: TokenUsagePieProps) {
  // Get context window for the model
  const contextWindow = CONTEXT_WINDOWS[model] || CONTEXT_WINDOWS['default'];

  // Calculate percentage of context window used
  const percentUsed = Math.min((sessionTokens / contextWindow) * 100, 100);
  const percentRemaining = 100 - percentUsed;

  // SVG circle parameters
  const sizeConfig = size === 'sm' ? { dim: 24, stroke: 3, radius: 9 } : { dim: 32, stroke: 4, radius: 12 };
  const { dim, stroke, radius } = sizeConfig;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (percentRemaining / 100);

  // Color based on usage
  const getColor = () => {
    if (percentUsed >= 90) return '#ef4444'; // red
    if (percentUsed >= 70) return '#f59e0b'; // warning/amber
    return '#14b8a6'; // teal/primary
  };

  return (
    <div className="flex items-center gap-2">
      {/* Pie Chart */}
      <div className="relative" style={{ width: dim, height: dim }}>
        <svg
          width={dim}
          height={dim}
          viewBox={`0 0 ${dim} ${dim}`}
          className="transform -rotate-90"
        >
          {/* Background circle */}
          <circle
            cx={dim / 2}
            cy={dim / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            className="text-surface-700"
          />
          {/* Progress circle */}
          <circle
            cx={dim / 2}
            cy={dim / 2}
            r={radius}
            fill="none"
            stroke={getColor()}
            strokeWidth={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            className="transition-all duration-300"
          />
        </svg>
      </div>

      {/* Text */}
      <div className="flex flex-col">
        <span className="text-xs text-text-secondary leading-tight">
          {sessionTokens > 0 ? `~${formatTokens(sessionTokens)}` : '0'}
          <span className="text-text-muted"> / {formatTokens(contextWindow)}</span>
        </span>
        <span className="text-[10px] text-text-muted leading-tight">
          {percentUsed.toFixed(1)}% context
        </span>
      </div>
    </div>
  );
}
